import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO, addMonths, startOfMonth, endOfMonth } from 'date-fns';
import { it } from 'date-fns/locale';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { CalendarRange, Upload, X, Loader2, Check, AlertTriangle, Facebook, Instagram, ImageOff, ChevronDown, ChevronUp, Palette, TrendingUp, RefreshCw } from 'lucide-react';
import TikTokIcon from '../icons/TikTokIcon';
import DesignerPanel from './DesignerPanel';

const PLATFORMS = [
  { value: 'facebook', label: 'Facebook', Icon: Facebook },
  { value: 'instagram', label: 'Instagram', Icon: Instagram },
  { value: 'tiktok', label: 'TikTok', Icon: TikTokIcon },
];
const PILLARS = { promo: 'Promo', educativo: 'Consigli', social_proof: 'Social proof', dietro_le_quinte: 'Dietro le quinte', ricorrenza: 'Ricorrenza' };

const toLocalInput = (iso) => {
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const PlanItemCard = ({ item, api, onChange, onRegenerate }) => {
  const [open, setOpen] = useState(false);
  const [designer, setDesigner] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const score = item.review?.score;
  const scoreColor = score >= 80 ? 'text-emerald-600' : score >= 60 ? 'text-amber-600' : 'text-red-600';

  const regenerate = async (instruction) => {
    setRegenerating(true);
    const ok = await onRegenerate(instruction);
    setRegenerating(false);
    if (ok) setFeedback('');
  };
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${item.include ? '' : 'opacity-50'}`} data-testid={`plan-item-${item.index}`}>
      <div className="flex items-start gap-3">
        <Checkbox checked={item.include} onCheckedChange={v => onChange({ include: !!v })} className="mt-1" data-testid={`plan-item-include-${item.index}`} />
        <div className="w-14 h-14 rounded-md overflow-hidden border bg-muted flex items-center justify-center shrink-0">
          {item.media_urls?.[0]
            ? <img src={`${api.replace('/api', '')}${item.media_urls[0]}`} alt="" className="w-full h-full object-cover" />
            : <ImageOff className="w-5 h-5 text-muted-foreground" />}
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <Badge variant="secondary" className="text-[10px]">{PILLARS[item.pillar] || item.pillar || '—'}</Badge>
            {item.post_format === 'story' && <Badge variant="outline" className="text-[10px]">Story</Badge>}
            {!item.media_urls?.length && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 gap-1"><AlertTriangle className="w-3 h-3" /> Aggiungi foto</Badge>}
            {score != null && <span className={`ml-auto font-semibold ${scoreColor}`}>{score}/100</span>}
          </div>
          <p className="text-sm line-clamp-2">{item.content}</p>
          <div className="flex flex-wrap items-center gap-2">
            <Input type="datetime-local" value={item.scheduled_local} onChange={e => onChange({ scheduled_local: e.target.value })} className="h-8 text-xs w-52" data-testid={`plan-item-date-${item.index}`} />
            <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setOpen(o => !o)} data-testid={`plan-item-toggle-${item.index}`}>
              {open ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />} {open ? 'Chiudi' : 'Modifica testo'}
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={() => regenerate('')} disabled={regenerating} data-testid={`plan-item-regenerate-${item.index}`}>
              {regenerating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Rigenera
            </Button>
            {item.media_urls?.[0] && !designer && (
              <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setDesigner(true)} data-testid={`plan-item-designer-${item.index}`}>
                <Palette className="w-3.5 h-3.5 mr-1" /> Designer
              </Button>
            )}
          </div>
        </div>
      </div>
      {designer && item.media_urls?.[0] && (
        <div className="pl-8">
          <DesignerPanel mediaUrl={item.source_media_url || item.media_urls[0]} defaultHeadline={item.headline || ''} defaultFormat={item.post_format === 'story' ? 'story' : 'feed'}
            onApply={(url) => { onChange({ media_urls: [url], source_media_url: item.source_media_url || item.media_urls[0] }); setDesigner(false); }} onCancel={() => setDesigner(false)} />
        </div>
      )}
      {open && (
        <div className="space-y-2 pl-8">
          <Textarea rows={8} value={item.content} onChange={e => onChange({ content: e.target.value })} data-testid={`plan-item-content-${item.index}`} />
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={feedback} onChange={e => setFeedback(e.target.value)} placeholder="Cosa cambiare? es. più ironico, cita il prezzo, tono più breve"
              className="h-8 text-xs" data-testid={`plan-item-feedback-${item.index}`} />
            <Button type="button" variant="outline" size="sm" className="h-8 text-xs shrink-0" onClick={() => regenerate(feedback)} disabled={regenerating} data-testid={`plan-item-regenerate-feedback-${item.index}`}>
              {regenerating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Riscrivi con indicazione
            </Button>
          </div>
          {item.review?.issues?.length > 0 && (
            <ul className="space-y-1">
              {item.review.issues.map((i, idx) => (
                <li key={idx} className="text-xs flex items-start gap-1.5">
                  <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${i.severity === 'high' ? 'text-red-500' : i.severity === 'medium' ? 'text-amber-500' : 'text-muted-foreground'}`} /> {i.text}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const AIPlanDialog = ({ open, onOpenChange, onSaved, usage }) => {
  const { API, getAuthHeaders } = useAuth();
  const nextMonth = addMonths(new Date(), 1);
  const [phase, setPhase] = useState('form');
  const [briefText, setBriefText] = useState('');
  const [postsCount, setPostsCount] = useState(8);
  const [startDate, setStartDate] = useState(format(startOfMonth(nextMonth), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfMonth(nextMonth), 'yyyy-MM-dd'));
  const [platforms, setPlatforms] = useState(['instagram']);
  const [media, setMedia] = useState([]);
  const [consent, setConsent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [performance, setPerformance] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/ai-social/performance`, getAuthHeaders()).then(r => setPerformance(r.data)).catch(() => setPerformance(null));
  }, [open, API, getAuthHeaders]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const reset = () => {
    clearInterval(pollRef.current);
    setPhase('form'); setBriefText(''); setPostsCount(8); setPlatforms(['instagram']); setMedia([]); setConsent(false); setJob(null); setItems([]);
  };
  const handleClose = (o) => { if (!o) reset(); onOpenChange(o); };
  const togglePlatform = (p) => setPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/upload/media`, fd, { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } });
        setMedia(prev => [...prev, { id: res.data.id, url: res.data.url, media_type: res.data.media_type, thumbnail_url: res.data.thumbnail_url }]);
      } catch (err) {
        toast.error(`Errore caricamento ${file.name}: ${err.response?.data?.detail || err.message}`);
      }
    }
    setUploading(false);
    e.target.value = '';
  };

  const startPolling = (jobId) => {
    clearInterval(pollRef.current);
    let failures = 0;
    let deadline = Date.now() + 12 * 60 * 1000;
    pollRef.current = setInterval(async () => {
      if (Date.now() > deadline) {
        clearInterval(pollRef.current);
        toast.error('La generazione del piano sta impiegando troppo tempo. Riprova con meno post.');
        setPhase('form');
        return;
      }
      try {
        const res = await axios.get(`${API}/ai-social/jobs/${jobId}`, getAuthHeaders());
        setJob(res.data);
        if (res.data.progress?.stage === 'queued') deadline = Date.now() + 12 * 60 * 1000;
        if (res.data.status === 'done') {
          clearInterval(pollRef.current);
          setItems((res.data.result?.items || []).map(it => ({
            ...it,
            include: true,
            content: `${it.caption}\n\n${(it.hashtags || []).join(' ')}`.trim(),
            scheduled_local: toLocalInput(it.scheduled_time),
          })));
          setPhase('result');
          const failed = res.data.result?.failed || 0;
          if (failed > 0) toast.warning(`${failed} post su ${res.data.result?.requested} non è stato generato: puoi rigenerarlo o scriverlo a mano.`);
        } else if (res.data.status === 'error') {
          clearInterval(pollRef.current);
          toast.error(res.data.error || 'Generazione del piano fallita');
          setPhase('form');
        }
      } catch {
        failures += 1;
        if (failures >= 4) {
          clearInterval(pollRef.current);
          toast.error('Errore nel recupero del piano: il server non risponde. Il piano potrebbe completarsi comunque, riprova tra poco.');
          setPhase('form');
        }
      }
    }, 2500);
  };

  const generate = async () => {
    if (!briefText.trim()) { toast.error('Descrivi il tema del periodo'); return; }
    if (!platforms.length) { toast.error('Seleziona almeno una piattaforma'); return; }
    if (media.length && !consent) { toast.error('Conferma di avere il consenso a pubblicare le foto'); return; }
    setPhase('running'); setJob(null);
    try {
      const res = await axios.post(`${API}/ai-social/plans`, {
        brief_text: briefText, posts_count: Number(postsCount), start_date: startDate, end_date: endDate, platforms, media_urls: media.map(m => m.url),
      }, getAuthHeaders());
      startPolling(res.data.job_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore avvio piano');
      setPhase('form');
    }
  };

  const updateItem = (index, patch) => setItems(prev => prev.map(it => it.index === index ? { ...it, ...patch } : it));

  const regenerateItem = async (item, instruction) => {
    try {
      const res = await axios.post(`${API}/ai-social/plans/regenerate-item`, {
        brief_text: briefText, platforms: item.platforms, post_format: item.post_format,
        pillar: item.pillar, angle: item.angle, key_message: item.headline || item.angle,
        media_url: item.source_media_url || item.media_urls?.[0] || null,
        previous_caption: item.caption, feedback: instruction || null,
      }, getAuthHeaders());
      const d = res.data;
      updateItem(item.index, {
        caption: d.caption, headline: d.headline, hashtags: d.hashtags, review: d.review,
        content: `${d.caption}\n\n${(d.hashtags || []).join(' ')}`.trim(),
      });
      toast.success('Didascalia rigenerata');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rigenerazione non riuscita');
      return false;
    }
  };

  const save = async () => {
    const selected = items.filter(it => it.include);
    if (!selected.length) { toast.error('Seleziona almeno un post'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/ai-social/plans/${job.id}/save`, {
        items: selected.map(it => ({
          index: it.index, content: it.content, scheduled_time: new Date(it.scheduled_local).toISOString(),
          platforms: it.platforms, post_format: it.post_format, media_urls: it.media_urls || [],
        })),
      }, getAuthHeaders());
      const n = res.data.created.length;
      toast.success(res.data.pending_approval ? `${n} post inviati al negozio per approvazione` : `${n} post programmati nel calendario`);
      onSaved?.();
      handleClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio del piano');
    } finally {
      setSaving(false);
    }
  };

  const progress = job?.progress || { done: 0, total: postsCount };
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const missingMedia = items.filter(it => it.include && !it.media_urls?.length).length;
  const remaining = usage?.remaining;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ai-plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2"><CalendarRange className="w-5 h-5 text-primary" /> Piano editoriale AI</DialogTitle>
          <DialogDescription>
            {phase === 'form' && 'Un tema per il periodo, le tue foto, e gli agenti costruiscono il calendario completo.'}
            {phase === 'running' && 'Gli agenti stanno scrivendo il piano: può richiedere qualche minuto.'}
            {phase === 'result' && 'Rivedi i post, sistema date e testi, poi salva nel calendario.'}
          </DialogDescription>
        </DialogHeader>

        {phase === 'form' && (
          <div className="space-y-5 mt-2">
            <div className="space-y-2">
              <Label>Tema del periodo *</Label>
              <Textarea data-testid="plan-brief-input" rows={3} value={briefText} onChange={e => setBriefText(e.target.value)}
                placeholder="es. Ottobre: rientro dalle vacanze, ricostruzione capelli danneggiati dal sole, nuovi colori autunnali, promo -15% sui trattamenti, Halloween" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Numero di post</Label>
                <Input type="number" min={2} max={16} value={postsCount} onChange={e => setPostsCount(e.target.value)} data-testid="plan-count-input" />
                {remaining != null && !usage?.acting_as && <p className="text-xs text-muted-foreground">Disponibili questo mese: {remaining}</p>}
              </div>
              <div className="space-y-2">
                <Label>Dal</Label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="plan-start-input" />
              </div>
              <div className="space-y-2">
                <Label>Al</Label>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="plan-end-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Canali</Label>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map(({ value, label, Icon }) => (
                  <Badge key={value} variant={platforms.includes(value) ? 'default' : 'outline'} className="cursor-pointer py-1.5 px-3 gap-1.5" onClick={() => togglePlatform(value)} data-testid={`plan-platform-${value}`}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Foto per il periodo (opzionale, max 8)</Label>
              <div className="flex flex-wrap gap-2">
                {media.map((m, idx) => (
                  <div key={m.id} className="relative w-16 h-16 rounded-lg overflow-hidden border bg-muted">
                    <img src={`${API.replace('/api', '')}${m.media_type === 'video' && m.thumbnail_url ? m.thumbnail_url : m.url}`} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setMedia(prev => prev.filter((_, i) => i !== idx))} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5" data-testid={`plan-media-remove-${idx}`}><X className="w-3 h-3" /></button>
                  </div>
                ))}
                {media.length < 8 && (
                  <label className="w-16 h-16 rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 text-muted-foreground">
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="plan-media-upload-input" />
                  </label>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Ogni foto viene assegnata a un post. I post senza foto restano da completare prima della pubblicazione.</p>
              {media.length > 0 && (
                <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
                  <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5" data-testid="plan-consent-checkbox" />
                  Confermo di avere il consenso delle persone ritratte a pubblicare queste immagini.
                </label>
              )}
            </div>
            <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-xs text-muted-foreground" data-testid="plan-performance-hint">
              <TrendingUp className="w-4 h-4 text-primary shrink-0" />
              {performance?.total > 0
                ? <span>Lo Strategist userà i risultati di <strong>{performance.total}</strong> post pubblicati{performance.by_pillar?.[0] ? <>: funzionano meglio i contenuti <strong>{performance.by_pillar[0][0]}</strong></> : ''}{performance.by_day?.[0] ? <>, il <strong>{performance.by_day[0][0]}</strong></> : ''}.</span>
                : <span>Ancora nessun dato di performance: dopo i primi post pubblicati lo Strategist imparerà cosa funziona meglio.</span>}
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={generate} disabled={uploading} data-testid="plan-generate-btn"><CalendarRange className="w-4 h-4 mr-2" /> Genera piano</Button>
            </div>
          </div>
        )}

        {phase === 'running' && (
          <div className="py-10 space-y-4" data-testid="plan-running-state">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <p className="text-sm" data-testid="plan-running-label">
                {job?.progress?.stage === 'queued'
                  ? `In coda: un altro contenuto è in elaborazione${job?.queue_position > 1 ? ` (posizione ${job.queue_position})` : ''}. Parte automaticamente appena si libera.`
                  : job?.steps?.strategist !== 'done'
                    ? 'Lo Strategist sta distribuendo temi e date...'
                    : `Copywriter e Reviewer al lavoro: ${progress.done}/${progress.total} post pronti`}
              </p>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        {phase === 'result' && (
          <div className="space-y-4 mt-2" data-testid="plan-result-state">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="secondary">{items.filter(i => i.include).length} post selezionati</Badge>
              {missingMedia > 0 && <Badge variant="outline" className="text-amber-700 border-amber-300 gap-1"><AlertTriangle className="w-3 h-3" /> {missingMedia} senza foto</Badge>}
              <span className="text-muted-foreground text-xs ml-auto">{format(parseISO(startDate), 'd MMM', { locale: it })} – {format(parseISO(endDate), 'd MMM yyyy', { locale: it })}</span>
            </div>
            <div className="space-y-3">
              {items.map(it => (
                <PlanItemCard key={it.index} item={it} api={API}
                  onChange={patch => updateItem(it.index, patch)}
                  onRegenerate={instruction => regenerateItem(it, instruction)} />
              ))}
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setPhase('form')} data-testid="plan-back-btn">Modifica richiesta</Button>
              <Button onClick={save} disabled={saving} data-testid="plan-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Salva nel calendario
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AIPlanDialog;
